import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './AuthProvider';
import { showTemporaryMessage } from "./Message";
import { useNavigate, useLocation } from "react-router-dom";
import { QRCodeSVG } from 'qrcode.react';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import Hex from 'crypto-js/enc-hex';
import bankidblack from '../images/bankid_logo_black.png';
import NeumorphicContainer from './NeumorphicContainer';

import config from '../config';

/**
 * Detect if the user is on a mobile/tablet device.
 */
function isMobileDevice() {
	if (typeof navigator === 'undefined') return false;
	return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Compute HMAC-SHA256. Returns hex-encoded string.
 * Uses crypto-js (works on HTTP, unlike Web Crypto API which requires HTTPS).
 */
function hmacSHA256(secret, message) {
	return HmacSHA256(message, secret).toString(Hex);
}

const BankID = () => {
	const { t } = useTranslation();
	const { onLogin } = React.useContext(AuthContext);
	const navigate = useNavigate();

	// Auth state
	const [phase, setPhase] = useState('idle'); // idle | waiting | qr | done
	const [orderRef, setOrderRef] = useState(null);
	const [qrData, setQrData] = useState('');
	const pollIntervalRef = useRef(null);
	const qrIntervalRef = useRef(null);
	const qrSecondsRef = useRef(0);
	const qrStartTokenRef = useRef('');
	const qrStartSecretRef = useRef('');

	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const pending_token = queryParams.get('pending_token');

	// Clean up intervals on unmount
	useEffect(() => {
		return () => {
			if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
			if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
		};
	}, []);

	// Remove any persisted same-device order (used to resume polling after a redirect reload).
	const clearStoredBankidOrder = () => {
		sessionStorage.removeItem('bankid-order-ref');
		sessionStorage.removeItem('bankid-pending-token');
	};

	/**
	 * Generate the animated QR code data string.
	 * Format: bankid.{qrStartToken}.{time}.{hmac_sha256(qrStartSecret, time)}
	 */
	const updateQrCode = useCallback(() => {
		const time = qrSecondsRef.current;
		const authCode = hmacSHA256(qrStartSecretRef.current, String(time));
		const data = `bankid.${qrStartTokenRef.current}.${time}.${authCode}`;
		setQrData(data);
		qrSecondsRef.current += 1;
	}, []);

	/**
	 * Build the redirect URL for BankID app to return to after signing.
	 * On iOS, this is the current page URL. On Android, use null for auto-return.
	 */
	const buildRedirectUrl = () => {
		// For the app to return to the browser after BankID is done
		return encodeURIComponent(window.location.href);
	};

	const initiateAuth = async (method = 'other-device') => {
		const isSameDevice = method === 'same-device';
		setPhase(isSameDevice ? 'waiting' : 'qr');
		setQrData('');

		const url = config.TUUTUUM_BACKEND_ADMIN_API + '/bankid/auth';
		console.log('[BankID] Calling auth endpoint:', url);

		try {
			const response = await fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({}),
			});

			console.log('[BankID] Response status:', response.status);
			const data = await response.json();
			console.log('[BankID] Response data:', data);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${JSON.stringify(data)}`);
			}

			if (!data.orderRef || !data.autoStartToken) {
				showTemporaryMessage(t('bankid_auth_failed'));
				setPhase('idle');
				return;
			}

			setOrderRef(data.orderRef);

			if (isSameDevice) {
				// Persist the order so polling can resume if the same-device redirect
				// reloads this page when returning from the BankID app.
				sessionStorage.setItem('bankid-order-ref', data.orderRef);
				if (pending_token) sessionStorage.setItem('bankid-pending-token', pending_token);

				// Same device: open BankID app directly via deep link
				const redirectUrl = buildRedirectUrl();
				const bankidUrl = `https://app.bankid.com/?autostarttoken=${data.autoStartToken}&redirect=${redirectUrl}`;
				window.location.href = bankidUrl;

				// Start polling — BankID will redirect back when done
				pollStatus(data.orderRef);
			} else {
				// Desktop: show animated QR code
				if (!data.qrStartToken || !data.qrStartSecret) {
					showTemporaryMessage(t('bankid_auth_failed'));
					setPhase('idle');
					return;
				}

				qrStartTokenRef.current = data.qrStartToken;
				qrStartSecretRef.current = data.qrStartSecret;
				qrSecondsRef.current = 0;

				// Generate first QR immediately, then every 1s
				updateQrCode();
				qrIntervalRef.current = setInterval(updateQrCode, 1000);

				// Start polling for completion
				pollStatus(data.orderRef);
			}
		} catch (error) {
			console.error('BankID auth error:', error);
			showTemporaryMessage(t('bankid_error'));
			setPhase('idle');
		}
	};

	const cancelAuth = () => {
		if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
		if (qrIntervalRef.current) { clearInterval(qrIntervalRef.current); qrIntervalRef.current = null; }
		clearStoredBankidOrder();
		setOrderRef(null);
		setQrData('');
		setPhase('idle');
	};

	const handleLoginComplete = (data) => {
		clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
		clearInterval(qrIntervalRef.current); qrIntervalRef.current = null;
		setPhase('done');

		// pending_token may have been dropped from the URL by the same-device redirect
		// reload — fall back to the value stashed in sessionStorage.
		const effectivePendingToken = pending_token || sessionStorage.getItem('bankid-pending-token');
		clearStoredBankidOrder();

		const { token: loginToken, id: user_id, type: user_type, personal_number } = data.data;
		showTemporaryMessage(t("login_succeeded"));
		onLogin(loginToken, user_id, user_type, personal_number);

		if (effectivePendingToken) {
			navigate("/register_qr_code?pending_token=" + effectivePendingToken);
		} else if (user_type === 'PRINTER') {
			navigate("/printer");
		} else if (user_type === 'REGULAR') {
			navigate("/bearers");
		} else {
			navigate("/home");
		}
	};

	const pollStatus = (ref) => {
		pollIntervalRef.current = setInterval(async () => {
			try {
				const response = await fetch(config.TUUTUUM_BACKEND_ADMIN_API + '/bankid/collect', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ orderRef: ref }),
				});

				const data = await response.json();

				if (data.status === 'complete') {
					handleLoginComplete(data);
				} else if (data.status === 'failed') {
					clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
					clearInterval(qrIntervalRef.current); qrIntervalRef.current = null;
					clearStoredBankidOrder();
					showTemporaryMessage(t('bankid_auth_failed'), '', 'danger');
					setPhase('idle');
					setOrderRef(null);
					setQrData('');
				}
				// else: pending — keep polling
			} catch (error) {
				clearInterval(pollIntervalRef.current); pollIntervalRef.current = null;
				clearInterval(qrIntervalRef.current); qrIntervalRef.current = null;
				clearStoredBankidOrder();
				console.error(error);
				showTemporaryMessage(t('bankid_error'), '', 'danger');
				setPhase('idle');
				setOrderRef(null);
				setQrData('');
			}
		}, 2000);
	};

	// Resume an in-flight same-device order after the BankID app redirect reloads
	// this page (the in-memory polling state is lost on reload).
	useEffect(() => {
		const savedRef = sessionStorage.getItem('bankid-order-ref');
		if (savedRef) {
			setOrderRef(savedRef);
			setPhase('waiting');
			pollStatus(savedRef);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Spinner component
	const Spinner = () => (
		<div className="flex items-center justify-center py-6">
			<svg className="animate-spin h-8 w-8 text-cyan-600" viewBox="0 0 24 24" fill="none">
				<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
				<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
			</svg>
		</div>
	);

	return (
		<NeumorphicContainer>
			<img src={bankidblack} className="w-16 h-14 self-center mx-auto" alt="BankID" />
			<h2 className="text-center font-bold text-2xl mt-2">
				{pending_token ? t("please_login_to_register_your_qr_code") : t("login_with_bankid")}
			</h2>

			{/* IDLE: Two-choice — same device or other device */}
			{phase === 'idle' && (
				<div className="mt-5 w-full space-y-3">
					<button
						type="button"
						onClick={() => initiateAuth('same-device')}
						className="btn-main text-white px-4 py-3 w-full rounded-md text-center font-semibold text-base transition ease-in-out hover:scale-105 flex items-center justify-center gap-3"
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
						{t("bankid_same_device")}
					</button>
					<button
						type="button"
						onClick={() => initiateAuth('other-device')}
						className="bg-white text-gray-700 border-2 border-gray-300 px-4 py-3 w-full rounded-md text-center font-semibold text-base transition ease-in-out hover:border-gray-400 hover:bg-gray-50 flex items-center justify-center gap-3"
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2"/><path d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"/></svg>
						{t("bankid_other_device")}
					</button>
				</div>
			)}

			{/* MOBILE WAITING: BankID app is opening */}
			{phase === 'waiting' && (
				<div className="mt-4 flex flex-col items-center">
					<Spinner />
					<p className="mt-2 text-center text-sm text-gray-600">
						{t("bankid_waiting_for_app")}
					</p>
					<button
						type="button"
						onClick={cancelAuth}
						className="mt-4 bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 w-full rounded-md text-center font-semibold transition ease-in-out"
					>
						{t("cancel")}
					</button>
				</div>
			)}

			{/* DESKTOP QR: Show animated QR code */}
			{phase === 'qr' && (
				<div className="mt-4 flex flex-col items-center">
					{qrData ? (
						<div className="bg-white p-4 rounded-lg shadow-inner">
							<QRCodeSVG value={qrData} size={200} level="M" />
						</div>
					) : (
						<Spinner />
					)}
					<p className="mt-3 text-center text-sm text-gray-600">
						{t("bankid_scan_qr_with_app")}
					</p>
					<button
						type="button"
						onClick={cancelAuth}
						className="mt-3 bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 w-full rounded-md text-center font-semibold transition ease-in-out"
					>
						{t("cancel")}
					</button>
				</div>
			)}
		</NeumorphicContainer>
	);
}

export default BankID;
